import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { defaultNavigationOptions } from '../../entities/navigation/navigation-options.entity';
import { DEFAULT_PAGE_SIZE } from '../../helpers';
import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { RecordSearch } from '../../record/entities/record-search.entity';
import { RecordService } from '../../record/services/record.service';
import { OverviewOptionsService } from '../../services/store/overview-options.service';

import { TranslateService } from '@ngx-translate/core';

import { NavigationItem, NavigationItemIconPosition } from '@stylehaven/components';
import { RecordNavigationService } from '../../services/navigation/record-navigation.service';
import { OverviewPageCache, RecordIndex } from '../../state/overview-page-cache/overview-page-cache.entity';

import { OverviewOptions } from '../../state/overview/overview-options.entity';

export enum Direction {
    previous = 'previous',
    next = 'next'
}

@Injectable()
export class PrevNextRecordNavigationItemCreator {
    constructor(
        private readonly translateService: TranslateService,
        private navigationService: RecordNavigationService,
        private recordService: RecordService,
        private overviewOptionsService: OverviewOptionsService
    ) {}

    public getNavigationItems(
        overviewOptions: OverviewOptions,
        currentRecordIndex: RecordIndex
    ): Observable<NavigationItem[]> {
        return this.overviewOptionsService.getOverviewPageCache(overviewOptions.overview).pipe(
            take(1),
            switchMap(cache => {
                const prevItem$ = this.getNavItem(cache, currentRecordIndex, overviewOptions, Direction.previous);
                const nextItem$ = this.getNavItem(cache, currentRecordIndex, overviewOptions, Direction.next);
                return forkJoin([prevItem$, nextItem$]);
            }),
            map(items => items.filter((navItem): navItem is NavigationItem => !!navItem))
        );
    }

    private getNavItem(
        cache: OverviewPageCache | undefined,
        currentRecordIndex: RecordIndex,
        overviewOptions: OverviewOptions,
        direction: Direction
    ): Observable<NavigationItem | undefined> {
        const fromCurrentPage: ApiFragment | undefined = this.fetchFromCurrentPage(
            cache,
            currentRecordIndex,
            direction
        );
        if (fromCurrentPage) {
            return of(this.createNavItem(fromCurrentPage, direction, cache));
        }

        return this.fetchFromOtherPage(overviewOptions, currentRecordIndex, cache, direction).pipe(
            map(nextRecord => {
                return this.createNavItem(nextRecord, direction, cache);
            })
        );
    }

    private fetchFromCurrentPage(
        cache: OverviewPageCache | undefined,
        currentRecordIndex: RecordIndex,
        direction: Direction
    ): ApiFragment | undefined {
        if (cache?.pageCache?.[currentRecordIndex.page] === undefined) {
            return undefined;
        }
        const index =
            direction === Direction.next ? currentRecordIndex.indexInPage + 1 : currentRecordIndex.indexInPage - 1;
        return cache.pageCache[currentRecordIndex.page].results[index];
    }

    private fetchFromOtherPage(
        overviewOptions: OverviewOptions,
        currentRecordIndex: RecordIndex,
        cache: OverviewPageCache | undefined,
        direction: Direction
    ): Observable<ApiFragment | undefined> {
        const pageIndex = direction === Direction.next ? currentRecordIndex.page + 1 : currentRecordIndex.page - 1;
        if (pageIndex < 0) {
            return of(undefined);
        }
        const cachedPage = cache?.pageCache?.[pageIndex];
        if (cachedPage) {
            const index = direction === Direction.next ? 0 : cachedPage.results.length - 1;
            const cachedNext = cachedPage.results[index];
            return of(cachedNext);
        }

        const recordSearch: RecordSearch = {
            ...overviewOptions.recordSearch,
            StartIndex: pageIndex * (overviewOptions.recordSearch?.NrOfResults || DEFAULT_PAGE_SIZE)
        };
        return this.recordService.search(recordSearch).pipe(
            tap(result => this.overviewOptionsService.addPageToCache(overviewOptions.overview, pageIndex, result)),
            map(result =>
                direction === Direction.next ? result.results[0] : result.results[result.results.length - 1]
            )
        );
    }

    private createNavItem(
        record: ApiFragment | undefined,
        direction: Direction,
        cache: OverviewPageCache | undefined
    ): NavigationItem | undefined {
        if (record === undefined) {
            return undefined;
        }
        return {
            label: this.translateService.instant(`navigation.${direction}-result`),
            navigationLink: this.navigationService.getNavigationLink({
                record,
                navigationOptions: {
                    ...defaultNavigationOptions,
                    queryParams: {
                        ['overview']: cache?.overviewId
                    }
                }
            }),
            icon: direction === Direction.next ? 'angle-right' : 'angle-left',
            iconPosition:
                direction === Direction.next ? NavigationItemIconPosition.End : NavigationItemIconPosition.Start
        };
    }
}
